import React from 'react';
import { Shield, CheckCircle, Clock, ArrowRight } from 'lucide-react';

const AISection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Trusted',
      description: 'SAMANVAI integrates world-class data sources with proprietary analytics, insight frameworks, and purpose-built algorithms to equip CXOs with timely, relevant, and actionable intelligence.',
    },
    {
      icon: CheckCircle,
      title: 'Verified',
      description: 'No AI hallucinations. SAMANVAI provides verified organizational insights to generate customized Executive Dashboards, Strategic Reports, and Performance Recommendations.',
    },
    {
      icon: Clock,
      title: 'Timely',
      description: 'When industry benchmarks update or competitors report their financials, your executive teams will have access to the latest comparative insights immediately.',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              AI for Executive
              <span className="block text-emerald-400">Performance Intelligence</span>
            </h2>

            <div className="space-y-8 mt-10">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })};
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
              {/* Animated arrows decoration */}
              <div className="absolute -top-4 -right-4 w-32 h-32">
                <svg viewBox="0 0 100 100" className="w-full h-full text-emerald-500/30">
                  <path d="M20 80 L50 20 L80 80" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M30 70 L50 30 L70 70" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M40 60 L50 40 L60 60" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-white mb-6">Why Top CXOs Choose SAMANVAI</h3>
              
              <ul className="space-y-4">
                {[
                  'Unique Industry Insights and AI',
                  'Learning & Development: Executive Performance Skills',
                  'Change Management: Global Enterprise Organizations',
                  'G100 Technology and Consulting Experience',
                  '25 Years of Experience: Technology, Business Disruptions',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              <a
                href="/performanceiq"
                className="inline-flex items-center gap-2 mt-8 text-emerald-400 font-medium hover:text-emerald-300 transition-colors"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;
